import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  MenuItem,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useForm, Controller } from 'react-hook-form';
import { useEvents } from '../../contexts/EventContext';
import {
  EventFormData,
  EVENT_APP_CATEGORIES,
  EventAppCategory,
} from '../../types';
import { ROUTES } from '../../utils/constants';
import LocationField, { LocationFieldValue } from '../../components/events/LocationField';
import { useCentres } from '../../hooks/useCentres';

// Plan 4J: replaces the legacy free-text "category" with the App-facing
// AppCategory taxonomy (mirrors CRM @teamjak/shared EVENT_APP_CATEGORIES).
const APP_CATEGORY_LABELS: Record<EventAppCategory, string> = {
  Families: 'Families',
  Teens: 'Teens',
  Angels: 'Angels',
  Siblings: 'Siblings',
  Parents: 'Parents',
  Fundraising: 'Fundraising',
};

const CreateEventPage: React.FC = () => {
  const navigate = useNavigate();
  const { createEvent } = useEvents();
  const { centres, loading: centresLoading } = useCentres();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [locationValue, setLocationValue] = useState<LocationFieldValue>({
    locationKey: null,
    override: { placeName: '', addressLine1: '', postcode: '', lat: null, lng: null },
  });
  const [locationError, setLocationError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EventFormData>({
    defaultValues: {
      title: '',
      description: '',
      date: '',
      startTime: '',
      endTime: '',
      location: '',
      capacity: 50,
      // Legacy `category` field — Plan 4J persists the AppCategory verbatim
      // here, so display layers that read `event.category` show the same
      // value as `event.appCategory`. Default kept just so react-hook-form
      // has a non-empty initial value before the user picks one.
      category: 'Families',
      appCategory: 'Families',
      registrationDeadline: '',
      registrationDeadlineTime: '23:59',
      isActive: true,
      // Plan 4K Phase 2: empty string means "no centre"; mapped to null
      // in onSubmit so backend persists null.
      centreId: '',
    },
  });

  const onSubmit = async (data: EventFormData) => {
    // Validate the structured location selection ourselves — react-hook-form
    // doesn't see the LocationField's internal state.
    if (!locationValue.locationKey) {
      setLocationError('Location is required');
      return;
    }
    if (locationValue.locationKey === 'Outing' && !locationValue.override.placeName.trim()) {
      setLocationError('Place name is required for outings');
      return;
    }
    setLocationError(null);
    setIsSubmitting(true);

    const isOuting = locationValue.locationKey === 'Outing';
    const appCategory = (data.appCategory ?? 'Families') as EventAppCategory;
    const payload: EventFormData = {
      ...data,
      // Legacy free-text `location` column kept populated for any list/detail
      // surface that still reads it directly. New surfaces prefer the
      // structured `locationDisplayName` set by the backend resolver.
      location: isOuting
        ? locationValue.override.placeName
        : locationValue.locationKey ?? '',
      // Persist AppCategory verbatim into the legacy `category` column so
      // display layers (getCategoryLabel/getCategoryColor) show the user's
      // actual choice. No more lossy mapping to the legacy 5-value enum.
      category: appCategory,
      appCategory,
      locationKey: locationValue.locationKey,
      locationOverride: isOuting ? locationValue.override : null,
      // Plan 4K Phase 2: empty string ('No centre') becomes null on the wire.
      centreId: data.centreId ? data.centreId : null,
    };

    try {
      await createEvent(payload);
      setShowSuccess(true);
      setTimeout(() => {
        navigate(ROUTES.ADMIN_EVENTS);
      }, 1500);
    } catch (err) {
      console.error('Failed to create event:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(ROUTES.ADMIN_EVENTS)}
        sx={{ mb: 3 }}
      >
        Back to Events
      </Button>

      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Create Event
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Fill in the details to create a new event
      </Typography>

      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Event Title"
                  {...register('title', { required: 'Title is required' })}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  {...register('description', { required: 'Description is required' })}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="appCategory"
                  control={control}
                  rules={{ required: 'Category is required' }}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      select
                      label="Category"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      error={!!errors.appCategory}
                      helperText={errors.appCategory?.message}
                    >
                      {EVENT_APP_CATEGORIES.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {APP_CATEGORY_LABELS[cat]}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <LocationField
                  value={locationValue}
                  onChange={setLocationValue}
                  error={locationError}
                />
              </Grid>

              {/* Plan 4K Phase 2: Centre dropdown — sent App→CRM as CentreId */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="centreId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      select
                      label="Centre"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      helperText={
                        centresLoading
                          ? 'Loading centres…'
                          : 'Optional — assign this event to a CRM centre'
                      }
                      disabled={centresLoading}
                    >
                      <MenuItem value="">
                        <em>No centre</em>
                      </MenuItem>
                      {centres.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.region ? `${c.name} (${c.region})` : c.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              {/* Date & Time */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, mt: 2 }}>
                  Date & Time
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Event Date"
                  InputLabelProps={{ shrink: true }}
                  {...register('date', { required: 'Date is required' })}
                  error={!!errors.date}
                  helperText={errors.date?.message}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  InputLabelProps={{ shrink: true }}
                  {...register('startTime', { required: 'Start time is required' })}
                  error={!!errors.startTime}
                  helperText={errors.startTime?.message}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="time"
                  label="End Time"
                  InputLabelProps={{ shrink: true }}
                  {...register('endTime', { required: 'End time is required' })}
                  error={!!errors.endTime}
                  helperText={errors.endTime?.message}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Registration Deadline"
                  InputLabelProps={{ shrink: true }}
                  {...register('registrationDeadline', { required: 'Deadline is required' })}
                  error={!!errors.registrationDeadline}
                  helperText={errors.registrationDeadline?.message}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  type="time"
                  label="Deadline Time"
                  InputLabelProps={{ shrink: true }}
                  {...register('registrationDeadlineTime')}
                  helperText="Default: 11:59 PM"
                />
              </Grid>

              {/* Capacity */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, mt: 2 }}>
                  Capacity & Settings
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Maximum Capacity"
                  {...register('capacity', {
                    required: 'Capacity is required',
                    min: { value: 1, message: 'Must be at least 1' },
                    valueAsNumber: true,
                  })}
                  error={!!errors.capacity}
                  helperText={errors.capacity?.message}
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          color="primary"
                        />
                      }
                      label="Event is active and visible to users"
                    />
                  )}
                />
              </Grid>

              {/* Submit */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Creating...' : 'Create Event'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate(ROUTES.ADMIN_EVENTS)}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={showSuccess}
        autoHideDuration={1500}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Event created successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateEventPage;
